import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  QrCodeScanner,
  CheckCircle,
  HelpOutline
} from '@mui/icons-material';
import { productsAPI } from '../services/api';
import { ScanHistory as ScanHistoryType } from '../types';

const ScanHistory: React.FC = () => {
  const [scanHistory, setScanHistory] = useState<ScanHistoryType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScanHistory();
  }, []);

  const loadScanHistory = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getMyScanHistory();
      // Sort by most recent first
      const sortedData = data.sort((a: any, b: any) => 
        new Date(b.scanned_at).getTime() - new Date(a.scanned_at).getTime()
      );
      setScanHistory(sortedData);
    } catch (error) {
      console.error('Error loading scan history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Istoricul Meu de Scanări
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Urmărește toate produsele pe care le-ai scanat
        </Typography>
      </Box>

      {scanHistory.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <QrCodeScanner sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Încă nu ai scanări
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Începe să scanezi produse pentru a vedea istoricul aici
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Status</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Produs</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Cod de Bare</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Scanat la</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scanHistory.map((scan, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {scan.product_name ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Found"
                            color="success"
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip
                            icon={<HelpOutline />}
                            label="Unknown"
                            color="default"
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {scan.product_name || 'Produs Necunoscut'}
                          </Typography>
                          {!scan.product_name && (
                            <Typography variant="caption" color="text.secondary">
                              Produsul nu a fost găsit în baza de date
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" fontFamily="monospace">
                          {scan.barcode}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <Typography variant="body2">
                          {new Date(scan.scanned_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      {scanHistory.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {scanHistory.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Scanări
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {scanHistory.filter(scan => scan.product_name).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Produse Găsite
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {scanHistory.filter(scan => !scan.product_name).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Produse Necunoscute
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default ScanHistory;
